import { formatDateString, selectAmount, formatter, numberFormatter, isVasTeam, displayName, formatWeight, isVasDesignerRole, isVasManagerRole, isStaffWithoutPriceView, isVasProdutionRole  } from 'app/main/apps/common/AuraFunctions';
import Box from '@mui/material/Box';
import { Checkbox, Chip, Tooltip } from '@mui/material';
import AuraTableIndeterminateCheckbox from 'app/main/apps/common/AuraTableIndeterminateCheckbox';
import TextFilter, { DateFilter, NumberFilter } from '../../common/FiltersComponent';
import OrdersSelectFilter, { ContainerNumberFilter, LoadSelectFilter, OrdersContainerShipmentFilter, OrdersTypeFilter, PaymentTypeFilter, RequestedByFilter, ShipmentTypeFilter, ShipToFilter, SupplierInvoiceStatusFilter } from './ColumnFilters';
import OrdersRowActions from './OrdersRowActions';
import OrderViewAction from '../../common/OrderViewAction';
import _ from '../../../../../@lodash';
import EditableInternalStatusCell from './EditableInternalStatusCell';
import ProgressCell from './ProgressCell';

// The row actions cell for one view filter. One component per filter value, kept for good: callers rebuild the
// columns on every render (EntitiesContent calls getColumns each time), and a new inline component each time
// would remount every row's actions -- and close any dialog open in one -- on each re-render.
const rowActionsCells = new Map();
function rowActionsCell(viewFilter) {
  if (!rowActionsCells.has(viewFilter)) {
    const RowActionsCell = (cellProps) => <OrdersRowActions {...cellProps} viewFilter={viewFilter} />;
    rowActionsCells.set(viewFilter, RowActionsCell);
  }
  return rowActionsCells.get(viewFilter);
}

function getColumns(isManage, type, selection, role, filterValue, isSupplierInvoice, user = null, selectedItems, selectedItemChange = null) {
  const typeName = isSupplierInvoice === true && "Supplier Invoice";
  const isCustomer = role === 'customer';

  const getTitle = () => {
    const title = _.capitalize(type)
    if (['fulfilled', 'in-production', 'approval', 'confirmed', 'container', 'container-management'].includes(type)) 
      return 'Order'
    if (['completed', 'rfd'].includes(type))
      return 'Rfp'
    return title
  }

  const hideContainernoOrCtn = () => {
    if (role === 'supplier') {
      return ['rfq', 'quote', 'rfd', 'completed', 'confirmed', 'order', 'in-production'].includes(type)
    }
    if (role === 'customer' && ['container'].includes(type)) {
      return true
    }
    if (isManage && ['container'].includes(type)) {
      return false
    }
    return 'order'
  }

  const hideContainernoForOrder = () => {
    return ['rfq', 'quote', 'rfd', 'completed', 'confirmed', 'in-production'].includes(type) || (type === "order" && (isVasDesignerRole(role) || isVasManagerRole(role)))
  }

  const hideRequestedBy = () => {
    let hide = false
    if (role !== 'customer') {
      hide = true
    } else if (!['rfq', 'quote', 'order'].includes(type)) {
      hide = true
    }
    return hide
  }


  const hideOrderType = () => {
    let hide = false
    if (role === 'customer') {
      hide =  true
    } else if (role === 'supplier') {
      hide = !['order', 'fulfilled', 'confirmed'].includes(type)
    }else{
      hide = !['rfq', 'quote', 'order'].includes(type)
    }
    return hide
  }

  const hideCompanyCodeColumn = () => {
    let hide = false
    if(role === 'supplier' && ((['inproduction_orders_view','shipped_orders_view'].includes(filterValue))  && (type === "in-production" || type === 'fulfilled'))){
      hide = false
    }
    else if(!isVasDesignerRole(role)){
      hide = true
    }
    return hide;
  }

  const getEtaHeader = () => {
    let header;
    if (type === 'rfq') {
      header = 'Quote ETA';
    } else if (['rfd', 'completed'].includes(type)) {
      header = 'Needed By';
    } else {
      header = 'Delivery ETA';
    }
    return header
  }

  const ellipsisOptions = {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textTransform: 'capitalize',
  }

  const HeaderType = `${_.capitalize(type)}`;
  const filterValueProp = !['supplier', 'supplier_archive'].includes(filterValue)
  const hideSupplierNameColumn = ['customer', 'customer_archive','back_order','reorder'].includes(filterValue) // to prevent duplicate customer name or supplier name during application of filter
  
  //  Hide status and workflow actions for back orders in staff view
  function hideStatusAndWorkflowActions() {
    if(isManage && ['back_order'].includes(filterValue)){
      return true;
    }
    return false;
  }

  //  Hide selection box for other orders except back orders in staff view
  function hideSelectionBox() {
    if(isManage && ['back_order','reorder'].includes(filterValue)){
      return false;
    }
    return true;
  }

  const shouldShowOrderNoCell =  ["container_orders_view"].includes(filterValue) && role === "customer" && type === "container";

  function getContainerNumberColumn() {
    const shouldShowWithoutCell =
      ["shipped_shipment_view"].includes(filterValue) &&
      role === "supplier" &&
      type === "fulfilled";

    let hideForTypeValue;
    if (shouldShowWithoutCell) {
      hideForTypeValue = false; // should show when second condition matches
    } else if (type === "container" || type === "fulfilled") {
      hideForTypeValue = false;
    } else {
      hideForTypeValue = isManage;
    }

    return {
      Header: "Container Number",
      accessor: "container_number",
      className: "",
      sxStyle: { width: 200, maxWidth: 200 },
      headerSxStyle: { width: 200, maxWidth: 200 },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      ...(shouldShowWithoutCell
        ? {} // second container number (shipped orders view)-> no Cell
        : {
            // first (without shipped orders view)-> with Cell
            Cell: ({ row, cell }) =>
              OrderViewAction({
                row,
                cell,
                stage: type,
                type: typeName,
              }),
          }),
      paramKey: "container_number",
      hideForType: hideForTypeValue,
    };
  }

  // Get the correct label for the "Paid Amount" field
  function getPaidAmtLabel() {
    if (isSupplierInvoice) return "Paid"; // Supplier Invoice table in staff login
    if (role === "supplier") return "Paid Amount"; // Invoice table header in supplier login
    return displayName?.invoicePaidAmt; // Invoice table in Customer and staff login
  }

  const orderColumns = [
    {
      id: "selection",
      className: "w-1",
      sortable: false,
      disablePadding: true,
      requiredPermissions: true,
      Header: ({ getToggleAllRowsSelectedProps }) => {
        // Only show the checkbox if hideSelectionBox() returns false
        if (!hideSelectionBox()) {
          return (
            <div>
              <AuraTableIndeterminateCheckbox
                {...getToggleAllRowsSelectedProps()}
              />
            </div>
          );
        }
        // Return null or empty fragment if condition is not met
        return null;
      },
      Cell: ({ row }) => (
        <div>
          {/* {row.original.amount && row.original.amount > 0 ? ( */}
          <AuraTableIndeterminateCheckbox
            {...row.getToggleRowSelectedProps()}
            onClick={(ev) => ev.stopPropagation()}
          />
          {/* ) : null} */}
        </div>
      ),
    },
    {
      Header: `${getTitle()}  No`,
      accessor: 'order_no',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: HeaderType === 'Approval' ? 150 : 80,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
       ...(shouldShowOrderNoCell
        ? {} // (container orders view)-> no Cell
        : {
            // first (without container orders view)-> with Cell
            Cell: ({ row, cell }) =>
              OrderViewAction({
                row,
                cell,
                stage: type,
                type: typeName,
                role
              }),
          }),
      paramKey: 'orderNo',
    },
    {
      Header: `Location`,
      accessor: 'location',
      className: '',
      sxStyle: {
        width: 190,
        maxWidth: 190,
      },
      headerSxStyle: {
        width: 190,
        maxWidth: 190,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'location',
      hideForType: !(type === 'container' && isCustomer),
    },
    // Need to map this , once filter is fixed
    {
      Header: filterValueProp ? 'Customer Name' : 'Supplier Name',
      accessor: 'name',
      className: '',
      sxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      headerSxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      sortable: true,
      showFilter: true,
      paramKey: filterValue?.includes('supplier') && type === 'order' ? 'supplier_name' : 'name',
      Cell: ({ row }) => {
        return (
          row.original.company_name && (
            <Tooltip title={row.original.company_name}>
              <div className='less-text'>
                {row.original.company_name}
              </div>
            </Tooltip>
          )
        );
      },
      Filter: TextFilter,
      requiredPermissions: true,
    },
    {
      Header: 'Customer Name',
      accessor: 'customer_name',
      className: '',
      sxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      headerSxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      hideForType: type === 'order' && filterValue?.includes('') ? hideSupplierNameColumn : true,
      sortable: true,
      showFilter: true,
      paramKey: 'name',
      Cell: ({ row }) => {
        return (
          row.original.customer_name && (
            <Tooltip title={row.original.customer_name}>
              <div className='less-text'>
                {row.original.customer_name}
              </div>
            </Tooltip>
          )
        );
      },
      Filter: TextFilter,
      requiredPermissions: true,
    },
    {
      Header: 'Customer Code',
      accessor: 'company_code',
      className: '',
      sxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      headerSxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      hideForType: hideCompanyCodeColumn(),
      sortable: true,
      showFilter: true,
      paramKey: 'company_code',
      Cell: ({ row }) => {
        return (
            <Tooltip title={row.original.company_code}>
              <div className='less-text'>
                {row.original.company_code}
              </div>
            </Tooltip>
        );
      },
      Filter: TextFilter,
    },
     // Created separate customer name which is used to display only in vas login 
    {
      Header: 'Customer Name',
      accessor: 'customer_name',
      className: '',
      sxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      headerSxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      hideForType: !isVasProdutionRole(role),
      sortable: true,
      showFilter: true,
      paramKey: 'name',
      Cell: ({ row }) => {
        return (
          row.original.customer_name && (
            <Tooltip title={row.original.customer_name}>
              <div className='less-text'>
                {row.original.customer_name}
              </div>
            </Tooltip>
          )
        );
      },
      Filter: TextFilter,
    },
    {
      Header: `${getTitle()}  Date`,
      accessor: 'date',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 120,
        maxWidth: 120,
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'date',
      hideForType: ['rfd', 'completed'].includes(type),
      Cell: ({ row }) => {
        if (row.original.date)
          return <div>{formatDateString(row.original.date)}</div>;
        return <div />
      },
    },
    {
      Header: 'Reference No',
      accessor: 'reference_no',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'reference',
      hideForType: !['order', 'rfq', 'quote'].includes(type) && role !== 'customer',
      Cell: ({ row }) => {
        return (
          row.original.customer_provider_number && (
            <Tooltip title={row.original.customer_provider_number}>
              <Box
                sx={{ ...ellipsisOptions }}
                style={{ textTransform: 'capitalize' }}
              >
                {isManage ? row.original.customer_provider_number : row.original.customer_provider_number}
              </Box>
            </Tooltip>
          )
        );
      },
    },
    {
      Header: 'Decedent',
      accessor: 'decedents',
      className: '',
      sxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      headerSxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      sortable: true,
      showFilter: true,
      hideForType: ['rfd', 'completed'].includes(type),
      Filter: TextFilter,
      paramKey: 'decedent',
      Cell: ({ row }) => {
        return (
          row.original.decedents && (
            <Tooltip title={row.original.decedents}>
              <div className='less-text'>
                {row.original.decedents}
              </div>
            </Tooltip>
          )
        );
      },
    },
    {
      Header: 'Comments',
      accessor: 'customer_notes',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: true,
      // PGSS-5444: the filter is hidden because there is nothing behind it. This box sent
      // `customer_notes` to /api/orders, and the backend has no such filter -- OrderController
      // populates 31 fields on the filter request and not one is note-related, and the
      // specification layer has no note predicate. So the box accepted input, narrowed nothing,
      // and left staff believing the records they wanted did not exist.
      //
      // A filter that silently returns the wrong set is worse than an absent one. Sorting still
      // works, and the column still shows its content.
      //
      // To bring it back, server-side filtering has to be built first: a field on
      // OrderFilterRequest, a predicate in OrderSpecification, and the parameter accepted by the
      // controller. That is a feature, not a repair -- tracked on the ticket.
      showFilter: false,
      hideForType: type !== 'order' || role === 'supplier',
      Filter: TextFilter,
      paramKey: 'customer_notes',
      Cell: ({ row }) => {
        return (
          <Tooltip title={row.original?.customer_notes} disableHoverListener={!row.original?.customer_notes}>
            <Box
              sx={{ ...ellipsisOptions }}
              style={{ textTransform: 'capitalize' }}
            >
              {row.original.customer_notes}
            </Box>
          </Tooltip>
          );
      },
    },
    {
      Header: 'Ship To',
      accessor: 'ship_to',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
      },
      sortable: true,
      showFilter: true,
      hideForType: !isCustomer || (isCustomer && !['order', 'quote', 'rfq','approval'].includes(type)) ,
      Filter: ShipToFilter,
      Cell: ({ row }) => {
        return (
          row.original.ship_to && (
            <Tooltip title={row.original.ship_to}>
              <Box
                sx={{ ...ellipsisOptions }}
                style={{ textTransform: 'capitalize' }}
              >
                {row.original.ship_to}
              </Box>
            </Tooltip>
          )
        );
      },
      paramKey: 'ship_to',
    },
    {
      Header: getEtaHeader(),
      accessor: 'eta_date',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 120,
        maxWidth: 120,
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'deliveryETA',
      Cell: ({ row }) => {
        if (row.original.eta_date)
          return <div>{formatDateString(row.original.eta_date)}</div>;
        return <div />
      },
    },
    {
      Header: 'Requested Date',
      accessor: 'date',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 140,
        maxWidth: 140,
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'deliveryETA',
      hideForType: !['rfd', 'completed'].includes(type),
      Cell: ({ row }) => {
        if (row.original.date)
          return <div>{formatDateString(row.original.date)}</div>;
        return <div />
      },
    },
    {
      Header: `${displayName?.weight}`,
      accessor: 'weight',
      className: 'w-1',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: true,
      showFilter: true,
      hideForType: ['rfq', 'order', 'rfd', 'completed'].includes(type),
      Filter: NumberFilter,
      paramKey: 'weight',
      requiredPermissions: true,
      Cell: ({ row }) => {
        return <div>{row.original.weight ? `${formatWeight(row.original.weight)}` : '-'} </div>;
      },
    },
    {
      Header: 'Qty',
      accessor: 'quantity',
      className: '',
      sxStyle: {
        width: 60,
        maxWidth: 60,
      },
      headerSxStyle: {
        width: role === 'customer' ? '60' : '80',
        maxWidth: role === 'customer' ? '60' : '80',
      },
      sortable: true,
      showFilter: true,
      Filter: NumberFilter,
      paramKey: 'quantity',
      hideForType: ['rfd', 'completed'].includes(type)
    },
    {
      Header: 'Amount',
      accessor: 'displayAmount',
      className: '',
      sxStyle: {
        width: 60,
        maxWidth: 800,
      },
      headerSxStyle: {
        width: role === 'customer' ? '60' : '120',
        maxWidth: role === 'customer' ? '60' : '120',
      },
      sortable: true,
      showFilter: true,
      hideForType: ['rfq', 'rfd', 'completed'].includes(type) || isVasTeam(role) || isStaffWithoutPriceView(user?.role_view),
      Filter: NumberFilter,
      paramKey: 'amount',
      Cell: ({ row }) => {
        return (
          <div>
            {formatter.format(selectAmount(row.original.all_invoices_pre_tax_total, row.original.displayAmount))}
          </div>
        );
      },
    },

    {
      Header: `Order Type`,
      accessor: 'order_type',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 800,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: false,
      showFilter: true,
      Filter: OrdersTypeFilter,
      // requiredPermissions: true,
      hideForType: hideOrderType(),
      paramKey: 'order_type',
    },

    {
      Header: `Container No`,
      accessor: 'container_number',
      className: '',
      sxStyle: {
        width: 120,
        maxWidth: 120,
      },
      headerSxStyle: {
        width: 120,
        maxWidth: 120,
      },
      sortable: false,
      showFilter: true,
      Filter: role === 'staff' || role === 'admin' ? ContainerNumberFilter: TextFilter,
      Cell: ({ row, cell }) => OrderViewAction({ row, cell, stage: type, rowValue: row.original.container_number, paramKey: 'container_number', styles: {...ellipsisOptions, display: 'block'}, type: typeName }),
      // requiredPermissions: true,
      hideForType: hideContainernoForOrder(),
      paramKey: 'container_number',
    },
    {
      Header: 'CTN',
      accessor: 'container_tracking_number',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 180,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
      },
      sortable: true,
      showFilter: true,
      hideForType: hideContainernoOrCtn(),
      // requiredPermissions: true,
      Filter: TextFilter,
      Cell: ({ row }) => {
        return <div>{row.original.container_tracking_number ? row.original.container_tracking_number : ''}</div>
      },
      paramKey: 'container_tracking_number',
    },
    {
      Header: 'Status',
      accessor: 'status.name',
      className: '',
      sxStyle: {
        width: 130,
        maxWidth: 130,
      },
      headerSxStyle: {
        width: 130,
        maxWidth: 130,
      },
      sortable: true,
      showFilter: true,
      filter: 'equals',
      Filter: OrdersSelectFilter,
      paramKey: 'status',
      type,
      hideForType: hideStatusAndWorkflowActions(),
      Cell: ({ row }) => {
        return (
          row.original.status && (
            <Tooltip title={row.original.status.name}>
              <Box
                sx={{ ...ellipsisOptions }}
                style={{ textTransform: 'capitalize' }}
              >
                {isCustomer ? row.original.status.display_name : row.original.status.name}
              </Box>
            </Tooltip>
          )
        );
      },
    },
    {
      Header: 'Internal Status',
      accessor: 'comments',
      className: '',
      sxStyle: {
        width: 160,
        maxWidth: 160,
        verticalAlign: "text-top",
      },
      headerSxStyle: {
        width: 160,
        maxWidth: 160,
        verticalAlign: "text-top",
      },
      sortable: false,
      showFilter: false,
      filter: 'equals',
      hideForType: !isCustomer || (isCustomer && !['order', 'quote', 'approval'].includes(type)),
      Filter: OrdersSelectFilter,
      paramKey: 'status',
      type,     
      Cell: EditableInternalStatusCell,
    },
    {
      Header: 'Drawing Required',
      accessor: 'drawing_required_icon',
      sxStyle: {
        width: "70px !important",
        maxWidth: "70px !important",
        whiteSpace: 'normal !important',
        wordWrap: 'break-word !important'
      },
      headerSxStyle: {
        width: "70px !important",
        maxWidth: "70px !important",
        verticalAlign: "text-top",
        whiteSpace: 'normal !important',
        wordWrap: 'break-word !important'
      },
      sortable: false,
      showFilter: false,
      hideForType: !(['rfq'].includes(type)) || !(['rfq'].includes(type) && role === "supplier"),
      Cell: ({ row }) => {
        return (
          <div style={{ width: "80px", maxWidth: "80px" }}>
            {row.original.drawing_required_icon && row.original.drawing_required_icon === true &&
              <Chip color="success" size="small" label="Yes" />
            }
          </div>
        );
      },
    },
    {
      Header: 'Revision Notes',
      accessor: 'revision_note',
      sortable: false,
      showFilter: false,
      sxStyle: {
        width: 130,
        maxWidth: 130,
      },
      headerSxStyle: {
        width: 130,
        maxWidth: 130,
      },
      hideForType: !(['rfq'].includes(type)) || !(['rfq'].includes(type) && role === "supplier"),
      Cell: ({ row }) => {
        return (
          row.original.revision_note && (
            <Tooltip title={row.original.revision_note}>
              <div className='less-text'>
                {row.original.revision_note}
              </div>
            </Tooltip>
          )
        );
      },
    },
    /* {
      Header: 'Quote For Revision',
      accessor: 'revised_notes',
      sxStyle: {
        width: "80px !important",
        maxWidth: "80px !important",
        verticalAlign: "text-top",
      },
      headerSxStyle: {
        width: "80px !important",
        maxWidth: "80px !important",
        verticalAlign: "text-top",
        whiteSpace: 'normal !important',
        wordWrap: 'break-word !important'
      },
      sortable: false,
      showFilter: false,
      hideForType: !(['rfq'].includes(type)) || !(['rfq'].includes(type) && role === "supplier"),
      Cell: ({ row }) => {
        return (
          <div style={{width: "80px", maxWidth: "80px"}}>
            { row.original.drawing_required_icon && row.original.drawing_required_icon === true &&
              <Tooltip title={row.original.revised_notes || ""}>
                <FuseSvgIcon size={24} color="success" className='fuse-list-item-icon shrink-0 text-24' >
                material-solid:announcement
                </FuseSvgIcon>
              </Tooltip>
            }
          </div>
        );
      },
    }, */
    {
      Header: `Requested By`,
      accessor: 'requested_by',
      className: '',
      sxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      headerSxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      sortable: false,
      showFilter: true,
      Filter: RequestedByFilter,
      hideForType: hideRequestedBy(),
      paramKey: 'requested_by',
      Cell: ({ row }) => {
        return (
          row.original.requested_by && (
            <Tooltip title={row.original.requested_by.name}>
              <div className='less-text'>
                {row.original.requested_by.name}
              </div>
            </Tooltip>
          )
        );
      },
    },
    {
      Header: 'Quote Sent/Received',
      accessor: 'sent_to_supplier_count',
      className: 'align-top',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: false,
      showFilter: false,
      hideForType: type !== 'rfq',
      requiredPermissions: true,
      Cell: ({ row }) => {
        if (row.original.sent_to_supplier_count)
          return (
            <div className='flex flex-row'>
              {`${row.original.sent_to_supplier_count} / ${row.original.replied_quote_count}`}
              {(row.original.rejected_quote_count && Number(row.original.rejected_quote_count) > 0) ? <div className='text-red font-bold pl-5'>{`(${row.original.rejected_quote_count} Rejected)`}</div> : <></>}
            </div>
          );
        return ''
      },
    },
    {
      id: 'action',
      className: 'w-1',
      sxStyle: {
        width: 100,
        maxWidth: 120,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: false,
      disableFilters: true,
      disablePadding: true,
      userManage: isManage,
      // PGSS-5367: the row actions need the view filter -- a supplier's Shipped Orders -> Orders View renders this
      // column set, and its Send Message (for statuses with no configured action, e.g. Pending For Receive
      // Shipment) is keyed on `viewFilter`. Without it the earlier fix never switched on and the cell stayed empty.
      Cell: rowActionsCell(filterValue),
      hideForType: hideStatusAndWorkflowActions(),
    },
  ];

  const invoiceColumns = [
    {
      id: 'selection',
      className: 'w-1',
      sortable: false,
      disablePadding: true,
      requiredPermissions: true,
      hideForType: !selection,
      Header: ({ getToggleAllRowsSelectedProps }) => (
        <div>{/* <AuraTableIndeterminateCheckbox {...getToggleAllRowsSelectedProps()} /> */}</div>
      ),
      Cell: ({ row }) => (
        <div>
          {row.original.amount && row.original.amount > 0 && (
            <AuraTableIndeterminateCheckbox
              {...row.getToggleRowSelectedProps()}
              onClick={(ev) => ev.stopPropagation()}
            />
          )}
        </div>
      ),
    },
    {
      Header: `${HeaderType}  No`,
      accessor: 'invoice_no',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 'max-content',
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 'max-content',
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      Cell: ({ row, cell }) => OrderViewAction({ row, cell, stage: type, type: typeName }),
      paramKey: 'invoice_no',
    },
    {
      Header: 'Order No',
      accessor: 'orderNo',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 180,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
      },
      sortable: true,
      showFilter: true,
      hideForType: role !== "customer",
      // requiredPermissions: true,
      Filter: TextFilter,
      Cell: ({ row }) => {
        return <div>{row.original.order_no ? row.original.order_no : ''}</div>
      },
      paramKey: 'orderNo',
    },
    {
      Header: 'Name',
      accessor: isSupplierInvoice ? 'supplier.name' : 'customer_name',
      className: '',
      sxStyle: {
        width: "110px",
        maxWidth: "110px",
      },
      headerSxStyle: {
        width: "110px",
        maxWidth: "110px",
      },
      sortable: true,
      showFilter: true,
      paramKey: 'name',
      Cell: ({ row }) => {
        if (isSupplierInvoice) {
          return (
            row?.original?.supplier?.name && (
              <Tooltip title={row.original.supplier.name}>
                <Box
                  sx={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textTransform: 'capitalize',
                  }}
                >
                  {row?.original?.supplier?.name}
                </Box>
              </Tooltip>
            )
          );
        }
        return (
          row.original.customer_name && (
            <Box
              sx={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textTransform: 'capitalize',
              }}
            >
              {row.original.customer_name}
            </Box>
          )
        );
      },
      Filter: TextFilter,
      requiredPermissions: true,
    },
    {
      Header: 'Customer',
      accessor: 'customer_name',
      className: '',
      sxStyle: {
        width: "100px",
        maxWidth: "100px",
      },
      headerSxStyle: {
        width: "100px",
        maxWidth: "100px",
        verticalAlign: "text-top !important",
      },
      sortable: false,
      showFilter: false,
      paramKey: 'customer_name',
      hideForType: role === "customer" || role === "supplier" || !isSupplierInvoice,
      Cell: ({ row }) => {
        return (
          row.original.customer_name && (
            <Tooltip title={row.original.customer_name}>
              <Box
                sx={{
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textTransform: "capitalize",
                }}
              >
                {row.original.customer_name}
              </Box>
            </Tooltip>
          )
        );
      },
      Filter: TextFilter,
      requiredPermissions: true,
    },
    {
      Header: `${HeaderType}  Date`,
      accessor: 'invoice_date',
      className: '',
      sxStyle: {
        width: "110px",
        maxWidth: "110px",
      },
      headerSxStyle: {
        width: "110px",
        maxWidth: "110px",
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'invoice_date',
      Cell: ({ row, cell }) => {
        if (cell.value)
          return <div>{formatDateString(cell.value)}</div>;
        return <div />
      },
    },
    {
      Header: `Due Date`,
      accessor: 'due_date',
      className: '',
      sxStyle: {
        width: "110px",
        maxWidth: "110px",
      },
      headerSxStyle: {
        width: "110px",
        maxWidth: "110px",
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'due_date',
      Cell: ({ row, cell }) => {
        if (cell.value)
          return <div>{formatDateString(cell.value)}</div>;
        return <div />
      },
    },
    {
      Header: `Shipped Date`,
      accessor: 'shipped_date',
      className: '',
      sxStyle: {
        width: "110px",
        maxWidth: "110px",
      },
      headerSxStyle: {
        width: "110px",
        maxWidth: "110px",
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      hideForType: role === "supplier" || isSupplierInvoice,
      paramKey: 'shipped_date',
      Cell: ({ row, cell }) => {
        if (cell.value)
          return <div>{formatDateString(cell.value)}</div>;
        return <div />
      },
    },
    {
      Header: `Order Type`,
      accessor: 'order_type',
      className: '',
      sxStyle: {
        width: "80px",
        maxWidth: "80px",
      },
      headerSxStyle: {
        width: "80px",
        maxWidth: "80px",
        verticalAlign: "text-top !important",
      },
      sortable: false,
      showFilter: false,
      Filter: OrdersTypeFilter,
      requiredPermissions: true,
      paramKey: 'order_type',
      hideForType: role === "customer" || role === "supplier" || !isSupplierInvoice,
    },
    {
      Header: 'Status',
      accessor: 'status.name',
      className: '',
      sxStyle: {
        width: 120,
        maxWidth: 120,
      },
      headerSxStyle: {
        width: 120,
        maxWidth: 120,
      },
      sortable: true,
      showFilter: true,
      filter: 'equals',
      Filter: isSupplierInvoice ? SupplierInvoiceStatusFilter : OrdersSelectFilter,
      paramKey: 'invoice_status',
      Cell: ({ row }) => {
        return (
          row.original.status && (
            <Tooltip title={row.original.status.name}>
              <Box
                sx={{
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textTransform: 'capitalize',
                }}
                style={{ textTransform: 'capitalize' }}
              >
                {isManage ? row.original.status.name : row.original.status.display_name}
              </Box>
            </Tooltip>
          )
        );
      },
      // requiredPermissions: true,
    },
    {
      Header: isSupplierInvoice ? 'Invoice' : 'Invoice Amount',
      accessor: 'invoice_amount',
      className: '',
      sxStyle: {
        width: "120px",
        maxWidth: "120px",
      },
      headerSxStyle: {
        width: "120px",
        maxWidth: "120px",
      },
      sortable: true,
      showFilter: true,
      Filter: NumberFilter,
      paramKey: 'invoice_amount',
      Cell: ({ row }) => <div>{formatter.format(row.original.invoice_amount)}</div>,
    },
    {
      Header: getPaidAmtLabel(),
      accessor: 'paid_amount',
      className: '',
      sxStyle: {
        width: "120px",
        maxWidth: "120px",
      },
      headerSxStyle: {
        width: "120px",
        maxWidth: "120px",
      },
      sortable: true,
      showFilter: true,
      Filter: NumberFilter,
      paramKey: 'paid_amount',
      Cell: ({ row }) => <div>{formatter.format(row.original.paid_amount)}</div>,
    },
    {
      Header: isSupplierInvoice ? 'Due' : 'Due Amount',
      accessor: 'due_amount',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: true,
      showFilter: true,
      Filter: NumberFilter,
      paramKey: 'due_amount',
      Cell: ({ row }) => <div>{formatter.format(row.original.due_amount)}</div>
    },
    {
      Header: 'Container No',
      accessor: 'container_number',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'container_number',
      Cell: ({ row, cell }) => OrderViewAction({ row, cell, stage: type, rowValue: row.original.container_number, paramKey: 'container_number', styles: {...ellipsisOptions, display: 'block'}, type: typeName }),
      hideForType: role === 'customer',
      // Cell: ({ row }) => <div>{row.original.container_number}</div>
    },
    {
      Header: 'CTN',
      accessor: 'container_tracking_number',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 120,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'container_tracking_number',
      hideForType: role === 'customer' || (isManage && !isSupplierInvoice),
      Cell: ({ row }) => {
        return (
          row.original.container_tracking_number && (
            <Tooltip title={row.original.container_tracking_number}>
              <Box
                sx={{
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {row.original.container_tracking_number}
              </Box>
            </Tooltip>
          )
        );
      },
    },
    {
      Header: 'Payment Type',
      accessor: 'customer_payment_type',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 120,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: false,
      showFilter: true,
      Filter: PaymentTypeFilter,
      paramKey: 'payment_type',
      hideForType: role === 'customer' || role === 'supplier' || isSupplierInvoice,
      Cell: ({ row }) => {
        return (
          row.original?.customer_payment_type && (
            <Tooltip title={row.original?.customer_payment_type}>
              <Box
                sx={{
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                {row.original?.customer_payment_type}
              </Box>
            </Tooltip>
          )
        );
      },
    },
    {
      id: 'action',
      className: 'w-1',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: false,
      disableFilters: true,
      disablePadding: true,
      userManage: isManage,
      Cell: OrdersRowActions,
    },
  ];

  const containerNumberColumn = getContainerNumberColumn();

  const shipmentColumns = [
    {
      id: 'selection',
      className: 'w-1',
      sortable: false,
      disablePadding: true,
      requiredPermissions: true,
      hideForType: type !== "container" || user?.role !== "admin", // Show Delete Shipment only for Admin and In cound container page
      Header: ({ getToggleAllRowsSelectedProps }) => (
        <div>{/* <AuraTableIndeterminateCheckbox {...getToggleAllRowsSelectedProps()} /> */}</div>
      ),
      Cell: ({ row }) => (

        <div>
          {/* {row.original.amount && row.original.amount > 0 ? ( */}
          <AuraTableIndeterminateCheckbox
            {...row.getToggleRowSelectedProps()}
            onClick={(ev) => ev.stopPropagation()}
          />
          {/* ) : null} */}
        </div>
      ),
    },
    containerNumberColumn,
    {
      Header: 'CTN',
      accessor: 'container_tracking_number',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      Cell: ({ row }) => {
        return row.original.container_tracking_number && (
        <Tooltip title={row.original.container_tracking_number}>
        <div style={{
          width: "90px",
          maxWidth: "90px",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          }}>{row.original.container_tracking_number}</div>
        </Tooltip>)
      },
      hideForType: hideContainernoOrCtn(),
      paramKey: 'container_tracking_number',
    },
    {
      Header: 'Name',
      accessor: 'name',
      className: '',
      sxStyle: {
        width: 190,
        maxWidth: 190,
      },
      headerSxStyle: {
        width: 190,
        maxWidth: 190,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      Cell: ({ row }) => {
        return <Tooltip title={row?.original?.name}>
          <div style={{ textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden'}}>
            {row.original.name}
          </div>
        </Tooltip>
      },
      paramKey: (role !== 'supplier' && role !== 'customer' && ['container'].includes(type)) ? "customer_name" : 'name',
      hideForType: (!isManage || !['container'].includes(type)),
    },
    {
      Header: 'Capacity',
      accessor: 'capacity',
      className: '',
      sxStyle: {
        width: 190,
        maxWidth: 190,
        verticalAlign: "text-top !important",
      },
      headerSxStyle: {
        width: 190,
        maxWidth: 190,
        verticalAlign: "text-top !important",
      },
      sortable: false,
      showFilter: false,
      Cell: ProgressCell,
      hideForType: !(type === 'container' && isManage)
    },
    {
      Header: `Order Date`,
      accessor: 'date',
      className: '',
      sxStyle: {
        width: "120px",
        maxWidth: "120px",
      },
      headerSxStyle: {
        width: "120px",
        maxWidth: "120px",
      },
      sortable: !(type === 'container' && isCustomer),
      Filter: DateFilter,
      showFilter: true,
      paramKey: 'date',
      hideForType: !(type === 'container' && isCustomer),
      Cell: ({ row, cell }) => {
        if (cell.value)
          return <Box sx={{
            width: '100px !important',
            maxWidth: '100px !important',
          }}>{formatDateString(cell.value)}</Box>;
        return <div />
      },
    },
    {
      Header: `Location`,
      accessor: 'location',
      className: '',
      sxStyle: {
        width: 190,
        maxWidth: 190,
      },
      headerSxStyle: {
        width: 190,
        maxWidth: 190,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'location',
      hideForType: !(type === 'container' && isCustomer),
    },
    {
      Header: `Shipment Date`,
      accessor: 'shipment_date',
      className: '',
      sxStyle: {
        width: "120px",
        maxWidth: "120px",
      },
      headerSxStyle: {
        width: "120px",
        maxWidth: "120px",
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'shipment_date',
      hideForType: (type === 'container' && isCustomer),
      Cell: ({ row, cell }) => {
        if (cell.value)
          return <Box sx={{
            width: '100px !important',
            maxWidth: '100px !important',
          }}>{formatDateString(cell.value)}</Box>;
        return <div />
      },
    },
    {
      Header: `ETA Date`,
      accessor: 'eta_date',
      className: '',
      sxStyle: {
        width: "100px",
        maxWidth: "100px",
      },
      headerSxStyle: {
        width: "100px",
        maxWidth: "100px",
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'eta_date',
      Cell: ({ row, cell }) => {
        if (cell.value)
          return <Box sx={{
            width: '100px !important',
            maxWidth: '100px !important',
          }}>{formatDateString(cell.value)}</Box>;
        return <div />
      },
    },
    {
      Header: `${displayName?.weight}`,
      accessor: 'weight',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: true,
      showFilter: true,
      Filter: NumberFilter,
      Cell: ({ row }) => {
        if (row.original.weight)
          return <div>{formatWeight(`${row.original.weight}`)}</div>;
        return <div />
      },
      paramKey: 'weight',
    },
    {
      Header: 'No of Orders',
      accessor: 'no_of_orders',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      Cell: ({ row }) => <div>{row.original.no_of_orders}</div>,
      // paramKey: 'no_of_orders',
    },
     {
      Header: 'No of Order Items',
      accessor: 'no_of_order_items',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      Filter: TextFilter,
      hideForType: !(type === 'container' && isCustomer),
      Cell: ({ row }) => <div>{row.original.no_of_order_items}</div>,
    },
    {
      Header: `Order Type`,
      accessor: 'order_type',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
      },
      sortable: false,
      showFilter: true,
      Filter: OrdersTypeFilter,
      hideForType: (!isManage || !['container'].includes(type)),
      paramKey: 'order_type',
    },
    {
      Header: `Location`,
      accessor: 'location',
      className: '',
      sxStyle: {
        width: "100px",
        maxWidth: "100px",
      },
      headerSxStyle: {
        width: "100px",
        maxWidth: "100px",
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'location',
      hideForType: (!isManage || !['container'].includes(type)),
      Cell: ({ row }) => {
        return row.original.location && (
        <Tooltip title={row.original.location}>
        <div style={{
          width: "90px",
          maxWidth: "90px",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          overflow: "hidden",
          }}>{row.original.location}</div>
        </Tooltip>)
      },
    },
    {
      Header: 'Status',
      accessor: 'status.name',
      sxStyle: {
        width: "180px",
        maxWidth: "180px",
      },
      headerSxStyle: {
        width: "180px",
        maxWidth: "180px",
        verticalAlign: "text-top !important",
      },
      sortable: true,
      showFilter: true,
      Filter: OrdersSelectFilter,
      Cell: ({ row }) => {
        return row?.original?.status?.name &&
        <Tooltip title={row?.original?.status?.name}>
          <div
            style={{
              display: "inline-block",
              maxWidth: "180px",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {isCustomer ? row.original.status.display_name : row.original.status.name}
          </div>
        </Tooltip>;
      },
      paramKey: "status",
      // PGSS-5671: lets the filter add "Shipment Completed" on Container Shipments with archive on.
      type,
    },
    {
      id: 'action',
      className: 'w-1',
      sxStyle: {
        width: '100px !important',
        maxWidth: '100px',
      },
      headerSxStyle: {
        width: '100px !important',
        maxWidth: '100px',
      },
      sortable: false,
      disableFilters: true,
      disablePadding: true,
      Cell: OrdersRowActions,
    },
  ]

  const outboundShipmentsColumns = [
    {
      id: 'selection',
      className: 'w-1',
      sortable: false,
      disablePadding: true,
      requiredPermissions: true,
      hideForType: type === 'outbound_shipments_customer' || user?.role !== "admin",
      Header: ({ getToggleAllRowsSelectedProps }) => (
        <div />
      ),
      Cell: ({ row }) => (
        <div id='checkbox-selection'>
          <Checkbox
            checked={selectedItems?.some(item => item.shipment_id === row.original.shipment_id)}
            onChange={(e) => {
              if (e.target.checked)
                selectedItemChange([...selectedItems, row.original])
              else selectedItemChange(selectedItems.filter(item => item.shipment_id !== row.original.shipment_id))
            }}
          />
        </div>
      ),
    },
    {
      Header: "Shipment No",
      accessor: "shipment_no",
      className: "font-medium",
      sxStyle: {
        width: 120,
        maxWidth: 120,
      },
      headerSxStyle: {
        width: 120,
        maxWidth: 120,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'shipment_no',
      Cell: ({ row, cell }) => OrderViewAction({ row, cell, stage: type}),
    },
    {
      Header: "Customer Name",
      accessor: "customer_name",
      sxStyle: {
        width: 150,
        maxWidth: 150,
      },
      headerSxStyle: {
        width: 150,
        maxWidth: 150,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'customer_name',
      hideForType: type === 'outbound_shipments_customer',
      Cell: ({ row }) => {
        return (
          row.original.customer_name && (
            <Tooltip title={row.original.customer_name}>
              <div className='crop-text'>
                {row.original.customer_name}
              </div>
            </Tooltip>
          )
        );
      },
    },
    {
      Header: "Shipment Date",
      accessor: "shipment_date",
      sxStyle: {
        width: 120,
        maxWidth: 120,
      },
      headerSxStyle: {
        width: 120,
        maxWidth: 120,
        verticalAlign: 'top',
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      Cell: ({ row, cell }) => {
          return cell.value && formatDateString(cell.value) ;
      },
      paramKey: 'shipment_date',
    },
    {
      Header: "Location",
      accessor: "location",
      sxStyle: {
        width: "160px",
        maxWidth: "160px",
      },
      headerSxStyle: {
        width: "160px",
        maxWidth: "160px",
        verticalAlign: 'top',
        whiteSpace: 'break-spaces !important'
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      hideForType: user?.role === "supplier",
      paramKey: 'location',
      Cell: ({ row }) => {
        return (
          row.original.location && (
            <Tooltip title={row.original.location}>
              <div className='crop-text'>
                {row.original.location}
              </div>
            </Tooltip>
          )
        );
      },
    },
    {
      Header: `${displayName?.weight}`,
      accessor: "weight",
      sxStyle: {
        width: 150,
        maxWidth: 150,
      },
      headerSxStyle: {
        width: 150,
        maxWidth: 150,
        verticalAlign: 'top',
      },
      sortable: false,
      showFilter: false,
      Cell: ({ row, cell }) => {
          return `${ formatWeight(cell.value) || '0'}` ;
      },
      paramKey: 'weight',
    },
    {
      Header: "No of Orders",
      accessor: "no_of_orders",
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
        verticalAlign: 'top',
        whiteSpace: 'break-spaces !important'
      },
      sortable: false,
      showFilter: false,
      Cell: ({ row }) => {
        return (
          row.original.no_of_orders && (
            <Tooltip title={row.original.no_of_orders}>
              <div className='crop-text'>
                {row.original.no_of_orders}
              </div>
            </Tooltip>
          )
        );
      },
    },
    {
      Header: "Shipment Type",
      accessor: "shipment_type",
      sxStyle: {
        width: 160,
        maxWidth: 160,
      },
      headerSxStyle: {
        width: 160,
        maxWidth: 160,
      },
      sortable: true,
      showFilter: true,
      Filter: ShipmentTypeFilter,
      paramKey: 'shipment_type',
      Cell: ({ row }) => {
        return (
          row.original.shipment_type && (
            <Tooltip title={row.original.shipment_type}>
              <div className='crop-text'>
                {row.original.shipment_type}
              </div>
            </Tooltip>
          )
        );
      },
    },
    {
      Header: "No of Pallets",
      accessor: "pallet_count",
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
        verticalAlign: 'top',
        whiteSpace: 'break-spaces !important'
      },
      sortable: false,
      showFilter: false,
      Cell: ({ row }) => {
        return (
          row.original.pallet_count && (
            <Tooltip title={row.original.pallet_count}>
              <div className='crop-text'>
                {row.original.pallet_count}
              </div>
            </Tooltip>
          )
        );
      },
    },
    // Carrier tracking (PRO) number captured at dispatch. Shown to staff and customers alike so a
    // customer can follow the shipment once it leaves the warehouse. Rendered as a link to the
    // carrier's tracking page when the backend resolved one (tracking_url), plain text otherwise.
    {
      Header: "Tracking #",
      accessor: "tracking_number",
      sxStyle: {
        width: 170,
        maxWidth: 170,
      },
      headerSxStyle: {
        width: 170,
        maxWidth: 170,
        verticalAlign: 'top',
        whiteSpace: 'break-spaces !important'
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'tracking_number',
      Cell: ({ row }) => {
        const { tracking_number: trackingNumber, tracking_url: trackingUrl, carrier } = row.original;
        if (!trackingNumber) return '';
        const tooltip = carrier ? `${carrier} - ${trackingNumber}` : trackingNumber;
        return (
          <Tooltip title={tooltip}>
            <div className='crop-text'>
              {trackingUrl ? (
                <a
                  href={trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline"
                  onClick={(ev) => ev.stopPropagation()}
                >
                  {trackingNumber}
                </a>
              ) : (
                trackingNumber
              )}
            </div>
          </Tooltip>
        );
      },
    },
    {
      Header: "Status",
      accessor: "status.name",
      sxStyle: {
        width: 200,
        maxWidth: 200,
      },
      headerSxStyle: {
        width: 200,
        maxWidth: 200,
        verticalAlign: 'top',
      },
      sortable: true,
      showFilter: true,
      Filter: OrdersSelectFilter,
      paramKey: 'status',
    },
    {
      id: "action",
      className: "w-2",
      sortable: false,
      disableFilters: true,
      disablePadding: true,
      Cell: OrdersRowActions,
    },
  ]

  // to display containers table only in supplier login
  const containerManagementColumns = [
    {
      id: 'selection',
      className: 'w-1',
      sortable: false,
      disablePadding: true,
      requiredPermissions: true,
      hideForType: type !== "container" || user?.role !== "admin", 
      Cell: ({ row }) => (

        <div>
          <AuraTableIndeterminateCheckbox
            {...row.getToggleRowSelectedProps()}
            onClick={(ev) => ev.stopPropagation()}
          />
        </div>
      ),
    },
    {
      Header: 'Container Number',
      accessor: 'container_number',
      className: '',
      sxStyle: {
        width: 200,
        maxWidth: 200,
      },
      headerSxStyle: {
        width: 200,
        maxWidth: 200,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      Cell: ({ row, cell }) => OrderViewAction({ row, cell, stage: type, type: typeName ,role}),
      paramKey: 'container_number',
    },
    {
      Header: `Customer Code`,
      accessor: 'customerCode',
      className: '',
      sxStyle: {
        width: 120,
        maxWidth: 120,
      },
      headerSxStyle: {
        width: 120,
        maxWidth: 120,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'customerCode',
    },
    {
      Header: `Order Type`,
      accessor: 'order_type',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
      },
      sortable: false,
      showFilter: true,
      Filter: OrdersTypeFilter,
      paramKey: 'order_type',
    },
    {
      Header: `Location`,
      accessor: 'location',
      className: '',
      sxStyle: {
        width: 190,
        maxWidth: 190,
      },
      headerSxStyle: {
        width: 190,
        maxWidth: 190,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'location',
    },
    
    {
      Header: 'Capacity',
      accessor: 'capacity',
      className: '',
      sxStyle: {
        width: 190,
        maxWidth: 190,
        verticalAlign: "text-top !important",
      },
      headerSxStyle: {
        width: 190,
        maxWidth: 190,
        verticalAlign: "text-top !important",
      },
      sortable: false,
      showFilter: false,
      Cell: ProgressCell,
    },
     {
      Header: 'No of Orders',
      accessor: 'no_of_orders',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      Cell: ({ row }) => <div>{row.original.no_of_orders}</div>,
      paramKey: 'no_of_orders',
    },
    {
      Header: `Shipment Date`,
      accessor: 'shipment_date',
      className: '',
      sxStyle: {
        width: '100px !important',
        maxWidth: '100px !important',
      },
      headerSxStyle: {
        width: '100px !important',
        maxWidth: '100px !important',
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'shipment_date',
      Cell: ({ row, cell }) => {
        if (cell.value)
          return <Box sx={{
            width: '100px !important',
            maxWidth: '100px !important',
          }}>{formatDateString(cell.value)}</Box>;
        return <div />
      },
    },
    {
      Header: `ETA Date`,
      accessor: 'eta_date',
      className: '',
      sxStyle: {
        width: '100px !important',
        maxWidth: '100px !important',
      },
      headerSxStyle: {
        width: '100px !important',
        maxWidth: '100px !important',
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'eta_date',
      Cell: ({ row, cell }) => {
        if (cell.value)
          return <Box sx={{
            width: '100px !important',
            maxWidth: '100px !important',
          }}>{formatDateString(cell.value)}</Box>;
        return <div />
      },
    },
    {
      Header: `Load Status`,
      accessor: 'load_status.name',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
      },
      sortable: true,
      showFilter: true,
      Filter: LoadSelectFilter,
      Cell: ({ row }) => <div style={{
        display: "inline-block",
        maxWidth: "250px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>{row?.original?.load_status?.name}</div>,
      paramKey: 'load_status',
    },
    {
      Header: 'Status',
      accessor: 'status.name',
      sxStyle: {
        width: 180,
        maxWidth: 180,
      },
      headerSxStyle: {
        width: 180,
        maxWidth: 180,
      },
      sortable: true,
      showFilter: true,
      Filter: OrdersContainerShipmentFilter,
      Cell: ({ row }) => <div style={{
        display: "inline-block",
        maxWidth: "250px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}>{row?.original?.status?.name}</div>,
      paramKey: 'status',
    },
    {
      id: 'action',
      className: 'w-1',
      sxStyle: {
        width: '100px !important',
        maxWidth: '100px',
      },
      headerSxStyle: {
        width: '100px !important',
        maxWidth: '100px',
      },
      sortable: false,
      disableFilters: true,
      disablePadding: true,
      Cell: OrdersRowActions,
    },
  ]

  const supplierOrderColumns = [
    {
      id: 'selection',
      className: 'w-1',
      sortable: false,
      disablePadding: true,
      requiredPermissions: true,
      // hideForType: !selection,
      Header: ({ getToggleAllRowsSelectedProps }) => (
        <div>{/* <AuraTableIndeterminateCheckbox {...getToggleAllRowsSelectedProps()} /> */}</div>
      ),
      Cell: ({ row }) => (

        <div>
          {/* {row.original.amount && row.original.amount > 0 ? ( */}
          <AuraTableIndeterminateCheckbox
            {...row.getToggleRowSelectedProps()}
            onClick={(ev) => ev.stopPropagation()}
          />
          {/* ) : null} */}
        </div>
      ),
    },
    {
      Header: `${getTitle()}  No`,
      accessor: 'order_no',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: HeaderType === 'Approval' ? 150 : 80,
      },
      sortable: true,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'orderNo',
    },
    {
      Header: 'Customer Code',
      accessor: 'company_code',
      className: '',
      sxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      headerSxStyle: {
        width: 150,
        maxWidth: 'max-content',
      },
      hideForType: hideCompanyCodeColumn(),
      sortable: true,
      showFilter: true,
      paramKey: 'company_code',
      Cell: ({ row }) => {
        return (
            <Tooltip title={row.original.company_code}>
              <div className='less-text'>
                {row.original.company_code}
              </div>
            </Tooltip>
        );
      },
      Filter: TextFilter,
    },
    // Need to map this , once filter is fixed
    {
      Header: `${getTitle()}  Date`,
      accessor: 'date',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 120,
        maxWidth: 120,
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'date',
      hideForType: ['rfd', 'completed'].includes(type),
      Cell: ({ row }) => {
        if (row.original.date)
          return <div>{formatDateString(row.original.date)}</div>;
        return <div />
      },
    },
    {
      Header: getEtaHeader(),
      accessor: 'eta_date',
      className: '',
      sxStyle: {
        width: 100,
        maxWidth: 100,
      },
      headerSxStyle: {
        width: 120,
        maxWidth: 120,
      },
      sortable: true,
      showFilter: true,
      Filter: DateFilter,
      paramKey: 'deliveryETA',
      Cell: ({ row }) => {
        if (row.original.eta_date)
          return <div>{formatDateString(row.original.eta_date)}</div>;
        return <div />
      },
    },
    {
      Header: 'Qty',
      accessor: 'quantity',
      className: '',
      sxStyle: {
        width: 60,
        maxWidth: 60,
      },
      headerSxStyle: {
        width: role === 'customer' ? '60' : '80',
        maxWidth: role === 'customer' ? '60' : '80',
      },
      sortable: true,
      showFilter: true,
      Filter: NumberFilter,
      paramKey: 'quantity',
      hideForType: ['rfd', 'completed'].includes(type)
    },
    {
      Header: 'Amount',
      accessor: 'displayAmount',
      className: '',
      sxStyle: {
        width: 60,
        maxWidth: 800,
      },
      headerSxStyle: {
        width: role === 'customer' ? '60' : '120',
        maxWidth: role === 'customer' ? '60' : '120',
      },
      sortable: true,
      showFilter: true,
      hideForType: ['rfq', 'rfd', 'completed'].includes(type) || isVasTeam(role) || isStaffWithoutPriceView(user?.role_view),
      Filter: NumberFilter,
      paramKey: 'amount',
      Cell: ({ row }) => {
        return (
          <div>
            {formatter.format(selectAmount(row.original.all_invoices_pre_tax_total, row.original.displayAmount))}
          </div>
        );
      },
    },

    {
      Header: `Order Type`,
      accessor: 'order_type',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 800,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: false,
      showFilter: true,
      Filter: OrdersTypeFilter,
      // requiredPermissions: true,
      hideForType: (role === 'supplier' || role === 'customer') ? !['order', 'fulfilled', 'confirmed'].includes(type) : !['rfq', 'quote', 'order'].includes(type),
      paramKey: 'order_type',
    },

    {
      Header: `Container No`,
      accessor: 'container_number',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 80,
      },
      headerSxStyle: {
        width: 100,
        maxWidth: 100,
      },
      sortable: false,
      showFilter: true,
      Filter: TextFilter,
      paramKey: 'container_number',
    },
    {
      Header: 'CTN',
      accessor: 'container_tracking_number',
      className: '',
      sxStyle: {
        width: 80,
        maxWidth: 180,
      },
      headerSxStyle: {
        width: 80,
        maxWidth: 80,
      },
      sortable: true,
      showFilter: true,
      hideForType: hideContainernoOrCtn(),
      // requiredPermissions: true,
      Filter: TextFilter,
      Cell: ({ row }) => {
        return <div>{row.original.container_tracking_number ? row.original.container_tracking_number : ''}</div>
      },
      paramKey: 'container_tracking_number',
    },
    {
      Header: 'Status',
      accessor: 'status.name',
      className: '',
      sxStyle: {
        width: 130,
        maxWidth: 130,
      },
      headerSxStyle: {
        width: 130,
        maxWidth: 130,
      },
      sortable: true,
      showFilter: true,
      filter: 'equals',
      Filter: OrdersSelectFilter,
      paramKey: 'status',
      type,
      Cell: ({ row }) => {
        return (
          row.original.status && (
            <Tooltip title={row.original.status.name}>
              <Box
                sx={{ ...ellipsisOptions }}
                style={{ textTransform: 'capitalize' }}
              >
                {isCustomer ? row.original.status.display_name : row.original.status.name}
              </Box>
            </Tooltip>
          )
        );
      },
    },
    // PGSS-5369: this set was the only order-shaped one with no workflow-action column, so a
    // supplier on In Production -> Orders View saw no actions at all, Send Message included.
    //
    // The column alone is not the fix. The actions are looked up by (stage, status), and both
    // In Production views share one URL -- so without the stage remap in OrdersRowActions this
    // renders the SHIPMENT action set against order rows, which for a supplier here includes
    // Delete Shipment and Mark as Completed and Shipped. The two changes ship together.
    {
      id: 'action',
      className: 'w-1',
      sxStyle: { width: 100, maxWidth: 100 },
      headerSxStyle: { width: 100, maxWidth: 100 },
      sortable: false,
      disableFilters: true,
      disablePadding: true,
      userManage: isManage,
      Cell: rowActionsCell(filterValue),
    }
  ];


  const getTypeColumns = (tableType) => {
    if (tableType === 'invoice') {
      return invoiceColumns;
    } 
    // to change table columns as per filter value when changed
    if((['inproduction_orders_view'].includes(filterValue)) && role === "supplier" && tableType === "in-production"){
      return supplierOrderColumns;
    }
    if((['shipped_shipment_view']).includes(filterValue) && role === "supplier"  && tableType === "fulfilled"){
      return shipmentColumns;
    }
    if((['container_orders_view'].includes(filterValue)) && role === "customer" && tableType === "container"){
      return orderColumns;
    }
    if (['in-production', 'shipment', 'container'].includes(tableType)) {
      return shipmentColumns;
    }
    if (tableType === 'outbound_shipments' || tableType === 'outbound_shipments_customer') {
      return outboundShipmentsColumns;
    }
    if (tableType === 'container-management') {
      return containerManagementColumns;
    }
    return orderColumns;
  };

  const columns = getTypeColumns(type)

  if (!isManage) {
    return columns.filter((column) => !column.requiredPermissions && !column.hideForType);
  }
  return columns.filter((column) => !column.isExtra && !column.hideForType);
}

export default getColumns;